<template>
  <div>
    <CRow class="mb-4 align-items-center">
      <CCol lg="6" md="12" class="mb-2 mb-lg-0">
        <h4 class="mb-0 text-primary font-weight-bold">
          <i class="fas fa-chart-pie"></i> Dashboard de Transporte
        </h4>
        <p class="text-muted small mb-0">Rendimiento financiero y estado de la flota.</p>
      </CCol>
      <CCol lg="6" md="12" class="text-right">
        <div class="d-flex justify-content-end align-items-center flex-wrap gap-2">
          <button class="btn btn-secondary btn-sm font-weight-bold" @click="returnHome">
            <i class="fas fa-arrow-left"></i> Volver
          </button>
        </div>
      </CCol>
    </CRow>

    <!-- FILTROS DE FECHA -->
    <CRow class="mb-4">
      <CCol lg="3" md="6" class="mb-2">
        <label class="small font-weight-bold text-muted">Fecha Desde</label>
        <input type="date" class="form-control form-control-sm" v-model="filters.date_from" @change="applyFilters">
      </CCol>
      <CCol lg="3" md="6" class="mb-2">
        <label class="small font-weight-bold text-muted">Fecha Hasta</label>
        <input type="date" class="form-control form-control-sm" v-model="filters.date_to" @change="applyFilters">
      </CCol>
      <CCol lg="2" md="6" class="mb-2 d-flex align-items-end">
        <button class="btn btn-sm btn-info font-weight-bold" @click="applyFilters">
          <i class="fas fa-search"></i> Aplicar Filtros
        </button>
      </CCol>
      <CCol lg="4" md="6" class="mb-2 d-flex align-items-end justify-content-end">
        <div class="text-muted small">
          <i class="fas fa-info-circle"></i> Datos actualizados al {{ currentDate }}
        </div>
      </CCol>
    </CRow>

    <!-- ✅ NUEVO: Aviso de la lógica de agrupación -->
    <CRow class="mb-3">
      <CCol md="12">
        <div class="alert alert-info small mb-0">
          <i class="fas fa-info-circle"></i>
          <strong>Nota:</strong> Todos los indicadores (ingresos, gastos, combustible y utilidad) se agrupan por la <strong>fecha del viaje</strong>. 
          Es decir, si un viaje inició el <strong>28/09</strong> y sus ventas/gastos se registraron días después, <u>igual se consideran dentro del rango</u> que incluya al viaje.
        </div>
      </CCol>
    </CRow>

    <!-- ============================================ -->
    <!-- FILA 1: INGRESOS, GASTOS, COMBUSTIBLE      -->
    <!-- ============================================ -->
    <CRow class="mb-4 text-center">
      <!-- Viajes Completados -->
      <CCol md="3" sm="6" class="mb-3">
        <CCard class="shadow-sm border-success h-100">
          <CCardBody class="p-3 d-flex flex-column justify-content-center">
            <div class="text-muted small text-uppercase font-weight-bold mb-1">
              <i class="fas fa-route text-success"></i> Viajes Completados
            </div>
            <h4 class="text-success mb-0 font-weight-bold">{{ kpis.total_viajes }}</h4>
          </CCardBody>
        </CCard>
      </CCol>

      <!-- Ingresos Totales -->
      <CCol md="3" sm="6" class="mb-3">
        <CCard class="shadow-sm border-primary h-100">
          <CCardBody class="p-3 d-flex flex-column justify-content-center">
            <div class="text-muted small text-uppercase font-weight-bold mb-1">
              <i class="fas fa-hand-holding-usd text-primary"></i> Ingresos Totales
            </div>
            <h4 class="text-primary mb-0 font-weight-bold">S/ {{ formatNumber(kpis.total_ingresos) }}</h4>
          </CCardBody>
        </CCard>
      </CCol>

      <!-- Combustible -->
      <CCol md="3" sm="6" class="mb-3">
        <CCard class="shadow-sm border-warning h-100">
          <CCardBody class="p-3 d-flex flex-column justify-content-center">
            <div class="text-muted small text-uppercase font-weight-bold mb-1">
              <i class="fas fa-gas-pump text-warning"></i> Combustible
            </div>
            <h4 class="text-warning mb-0 font-weight-bold">S/ {{ formatNumber(kpis.total_combustible) }}</h4>
            <small class="text-muted">Viajes del período</small>
          </CCardBody>
        </CCard>
      </CCol>

      <!-- Gastos del Chofer -->
      <CCol md="3" sm="6" class="mb-3">
        <CCard class="shadow-sm border-danger h-100">
          <CCardBody class="p-3 d-flex flex-column justify-content-center">
            <div class="text-muted small text-uppercase font-weight-bold mb-1">
              <i class="fas fa-tools text-danger"></i> Gastos del Chofer
            </div>
            <h4 class="text-danger mb-0 font-weight-bold">S/ {{ formatNumber(kpis.total_gastos) }}</h4>
            <small class="text-muted">Sin recargas</small>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <!-- ============================================ -->
    <!-- FILA 2: UTILIDAD DESTACADA                 -->
    <!-- ============================================ -->
    <CRow class="mb-4">
      <CCol md="12">
        <CCard class="shadow-sm" :class="kpis.utilidad >= 0 ? 'bg-success text-white' : 'bg-warning text-dark'">
          <CCardBody class="p-4 d-flex justify-content-between align-items-center flex-wrap">
            <div>
              <div class="text-uppercase font-weight-bold" style="font-size: 14px;">
                <i class="fas fa-chart-line"></i> Utilidad Neta del Período
              </div>
              <small style="opacity: 0.9;">
                Ingresos − Combustible − Gastos del Chofer
              </small>
              <br>
            </div>
            <h2 class="mb-0 font-weight-bold mt-2 mt-md-0">
              S/ {{ formatNumber(kpis.utilidad) }}
            </h2>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <!-- ============================================ -->
    <!-- FILA 3: ESTADO DE LA FLOTA                 -->
    <!-- ============================================ -->
    <CRow class="mb-4 text-center">
      <CCol md="3" sm="6" class="mb-3">
        <CCard class="shadow-sm border-info h-100">
          <CCardBody class="p-3 d-flex flex-column justify-content-center">
            <div class="text-muted small text-uppercase font-weight-bold mb-1">
              <i class="fas fa-truck text-info"></i> Vehículos Activos
            </div>
            <h4 class="text-info mb-0 font-weight-bold">{{ kpis.vehiculos_activos }}</h4>
          </CCardBody>
        </CCard>
      </CCol>

      <CCol md="3" sm="6" class="mb-3">
        <CCard class="shadow-sm border-danger h-100">
          <CCardBody class="p-3 d-flex flex-column justify-content-center">
            <div class="text-muted small text-uppercase font-weight-bold mb-1">
              <i class="fas fa-exclamation-triangle text-danger"></i> Vehículos con Alarmas
            </div>
            <h4 class="text-danger mb-0 font-weight-bold">{{ kpis.vehiculos_vencidos }}</h4>
          </CCardBody>
        </CCard>
      </CCol>

      <CCol md="3" sm="6" class="mb-3">
        <CCard class="shadow-sm border-secondary h-100">
          <CCardBody class="p-3 d-flex flex-column justify-content-center">
            <div class="text-muted small text-uppercase font-weight-bold mb-1">
              <i class="fas fa-route text-secondary"></i> Total Viajes
            </div>
            <h4 class="text-secondary mb-0 font-weight-bold">{{ kpis.total_viajes_generales }}</h4>
          </CCardBody>
        </CCard>
      </CCol>

      <CCol md="3" sm="6" class="mb-3">
        <CCard class="shadow-sm border-warning h-100">
          <CCardBody class="p-3 d-flex flex-column justify-content-center">
            <div class="text-muted small text-uppercase font-weight-bold mb-1">
              <i class="fas fa-clock text-warning"></i> Mantenimientos Pendientes
            </div>
            <h4 class="text-warning mb-0 font-weight-bold">{{ kpis.mantenimientos_pendientes }}</h4>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <!-- ============================================ -->
    <!-- GRÁFICOS                                   -->
    <!-- ============================================ -->
    <CRow>
      <!-- Gráfico de Barras: Ingresos vs Combustible vs Gastos -->
      <CCol lg="8" class="mb-4">
        <CCard class="shadow-sm border-0 h-100">
          <CCardHeader class="bg-dark text-white font-weight-bold">
            <i class="fas fa-chart-bar"></i> Flujo Mensual: Ingresos, Combustible y Gastos
          </CCardHeader>
          <CCardBody>
            <div v-if="isLoading" class="text-center py-5 text-muted">
              <i class="fas fa-spinner fa-spin fa-2x"></i>
            </div>
            <CChartBar
              v-else-if="chartLabels.length > 0"
              :datasets="barChartData"
              :options="barChartOptions"
              :labels="chartLabels"
              style="height: 300px;"
            />
            <div v-else class="text-muted font-weight-bold text-center py-5">
              No hay suficientes datos para graficar.
            </div>
          </CCardBody>
        </CCard>
      </CCol>

      <!-- Gráfico Circular: Ingresos por tipo de comprobante -->
      <CCol lg="4" class="mb-4">
        <CCard class="shadow-sm border-0 h-100">
          <CCardHeader class="bg-dark text-white font-weight-bold">
            <i class="fas fa-chart-pie"></i> Ingresos por Comprobante
          </CCardHeader>
          <CCardBody class="d-flex align-items-center justify-content-center">
            <div v-if="isLoading" class="text-center py-5 text-muted">
              <i class="fas fa-spinner fa-spin fa-2x"></i>
            </div>
            <div v-else-if="pieData.length == 0 || pieData.every(v => v === 0)" class="text-muted font-weight-bold text-center py-5">
              No hay datos disponibles.
            </div>
            <CChartPie
              v-else
              :datasets="pieChartData"
              :labels="pieLabels"
              :options="{ responsive: true, maintainAspectRatio: false }"
              style="height: 250px; width: 100%;"
            />
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <!-- ============================================ -->
    <!-- TABLA: DESGLOSE POR VIAJE                  -->
    <!-- ============================================ -->
    <CRow>
      <CCol lg="12" class="mb-4">
        <CCard class="shadow-sm border-0">
          <CCardHeader class="bg-success text-white font-weight-bold d-flex justify-content-between align-items-center">
            <span>
              <i class="fas fa-calculator"></i> Desglose de Utilidad por Viaje
            </span>
            <span class="badge badge-light text-dark">
              {{ tripsBreakdown.length }} viajes
            </span>
          </CCardHeader>
          <CCardBody>
            <div class="table-responsive">
              <table class="table table-hover table-striped text-center align-middle mb-0">
                <thead class="bg-dark text-white">
                  <tr>
                    <th>#</th>
                    <th>Fecha</th>
                    <th>Origen</th>
                    <th>Destino</th>
                    <th>Vehículo</th>
                    <th>Chofer</th>
                    <th class="text-right">Ventas (S/)</th>
                    <th class="text-right">Gastos (S/)</th>
                    <th class="text-right">Combustible (S/)</th>
                    <th class="text-right">Utilidad (S/)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(trip, index) in tripsBreakdown" :key="trip.trip_id">
                    <td>{{ index + 1 }}</td>
                    <td>{{ formatDate(trip.fecha_inicio) }}</td>
                    <td class="text-left">
                      <small>{{ trip.origen || '-' }}</small>
                    </td>
                    <td class="text-left">
                      <small>{{ trip.destino || '-' }}</small>
                    </td>
                    <td>
                      <span class="badge badge-secondary">{{ trip.vehicle_plate }}</span>
                    </td>
                    <td>
                      <small>{{ trip.driver_name }}</small>
                    </td>
                    <td class="text-right text-primary font-weight-bold">
                      S/ {{ formatNumber(trip.ventas) }}
                    </td>
                    <td class="text-right text-danger font-weight-bold">
                      S/ {{ formatNumber(trip.gastos) }}
                    </td>
                    <td class="text-right text-warning font-weight-bold">
                      S/ {{ formatNumber(trip.combustible) }}
                    </td>
                    <td class="text-right font-weight-bold"
                        :class="trip.utilidad >= 0 ? 'text-success' : 'text-danger'">
                      S/ {{ formatNumber(trip.utilidad) }}
                    </td>
                  </tr>
                  <tr v-if="tripsBreakdown.length === 0">
                    <td colspan="10" class="text-muted py-4">
                      No hay viajes registrados en el período seleccionado.
                    </td>
                  </tr>
                </tbody>
                <tfoot v-if="tripsBreakdown.length > 0" class="bg-light font-weight-bold">
                  <tr>
                    <td colspan="6" class="text-right text-dark">
                      SUBTOTALES:
                    </td>
                    <td class="text-right text-primary">
                      S/ {{ formatNumber(tripsTotales.ventas) }}
                    </td>
                    <td class="text-right text-danger">
                      S/ {{ formatNumber(tripsTotales.gastos) }}
                    </td>
                    <td class="text-right text-warning">
                      S/ {{ formatNumber(tripsTotales.combustible) }}
                    </td>
                    <td class="text-right"
                        :class="tripsTotales.utilidad >= 0 ? 'text-success' : 'text-danger'">
                      S/ {{ formatNumber(tripsTotales.utilidad) }}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

  </div>
</template>

<script>
import { mapActions } from "vuex";
import { CChartBar, CChartPie } from "@coreui/vue-chartjs";
import moment from "moment";

export default {
  name: "TransportDashboard",
  components: {
    CChartBar,
    CChartPie
  },
  data() {
    return {
      isLoading: false,
      filters: {
        date_from: moment().subtract(6, 'months').format("YYYY-MM-DD"),
        date_to: moment().format("YYYY-MM-DD")
      },
      kpis: {
        total_viajes: 0,
        total_ingresos: 0,
        total_gastos: 0,
        total_combustible: 0,
        utilidad: 0,
        mantenimientos_pendientes: 0,
        vehiculos_vencidos: 0,
        vehiculos_activos: 0,
        total_viajes_generales: 0
      },
      chartLabels: [],
      incomeData: [],
      expenseData: [],
      fuelData: [],
      pieLabels: [],
      pieData: [],
      recentTrips: [],

      // ✅ Desglose por viaje
      tripsBreakdown: [],
      tripsTotales: {
        ventas: 0,
        gastos: 0,
        combustible: 0,
        utilidad: 0,
      },

      barChartOptions: {
        maintainAspectRatio: false,
        responsive: true,
        tooltips: {
          callbacks: {
            label: function(t) { return 'S/ ' + parseFloat(t.yLabel).toFixed(2); }
          }
        },
        scales: {
          yAxes: [{ ticks: { beginAtZero: true } }]
        }
      }
    };
  },
  computed: {
    currentDate() {
      return moment().format("DD/MM/YYYY HH:mm");
    },
    barChartData() {
      return [
        { label: 'Ingresos (S/)',    backgroundColor: '#2eb85c', data: this.incomeData },
        { label: 'Combustible (S/)', backgroundColor: '#f9b115', data: this.fuelData },
        { label: 'Gastos (S/)',      backgroundColor: '#e55353', data: this.expenseData }
      ];
    },
    pieChartData() {
      const colors = ['#0d6efd', '#3399ff', '#f9b115'];
      return [{ backgroundColor: colors, data: this.pieData }];
    }
  },
  created() {
    this.applyFilters();
  },
  methods: {
    ...mapActions(["get", "post"]),

    formatNumber(value) {
      if (value === undefined || value === null) return '0.00';
      return Number(value).toFixed(2);
    },

    formatDate(date) {
      if (!date) return '-';
      return moment(date).format('DD/MM/YYYY');
    },

    getStatusBadge(status) {
      const badges = {
        'PENDIENTE': 'badge-warning',
        'EN_CURSO': 'badge-info',
        'EN_RUTA': 'badge-info',
        'COMPLETADO': 'badge-success',
        'ANULADO': 'badge-danger'
      };
      return badges[status] || 'badge-secondary';
    },

    getStatusLabel(status) {
      const labels = {
        'PENDIENTE': 'Pendiente',
        'EN_CURSO': 'En Curso',
        'EN_RUTA': 'En Ruta',
        'COMPLETADO': 'Completado',
        'ANULADO': 'Anulado'
      };
      return labels[status] || status;
    },

    returnHome() {
      this.$emit('returnHome');
    },

    applyFilters() {
      this.fetchDashboardData();
      this.fetchRecentTrips();
    },

    fetchDashboardData() {
      this.isLoading = true;
      this.post({
        url: `${this.$store.getters.get__url}/transporte/dashboard`,
        token: this.$store.getters.get__token,
        params: {
          date_from: this.filters.date_from,
          date_to: this.filters.date_to,
          campu_id: this.$store.getters.get_campu?.id || null
        }
      })
      .then(res => {
        let data = res.data || {};

        if (data.kpis) {
          this.kpis = {
            total_viajes: data.kpis.total_viajes || 0,
            total_ingresos: data.kpis.total_ingresos || 0,
            total_gastos: data.kpis.total_gastos || 0,
            total_combustible: data.kpis.total_combustible || 0,
            utilidad: data.kpis.utilidad || 0,
            mantenimientos_pendientes: data.kpis.mantenimientos_pendientes || 0,
            vehiculos_vencidos: data.kpis.vehiculos_vencidos || 0,
            vehiculos_activos: data.kpis.vehiculos_activos || 0,
            total_viajes_generales: data.kpis.total_viajes_generales || 0
          };
        }

        if (data.monthly && Array.isArray(data.monthly)) {
          this.chartLabels  = data.monthly.map(m => m.month_name || '-');
          this.incomeData   = data.monthly.map(m => Number(m.income) || 0);
          this.expenseData  = data.monthly.map(m => Number(m.expense) || 0);
          this.fuelData     = data.monthly.map(m => Number(m.fuel) || 0);
        } else {
          this.chartLabels = [];
          this.incomeData = [];
          this.expenseData = [];
          this.fuelData = [];
        }

        if (data.pie_data && data.pie_labels) {
          this.pieData   = data.pie_data;
          this.pieLabels = data.pie_labels;
        } else {
          this.pieData   = [];
          this.pieLabels = [];
        }

        // ✅ Desglose por viaje
        if (data.trips_breakdown && data.trips_breakdown.trips) {
          this.tripsBreakdown = data.trips_breakdown.trips;
          this.tripsTotales = data.trips_breakdown.totales || {
            ventas: 0,
            gastos: 0,
            combustible: 0,
            utilidad: 0,
          };
        } else {
          this.tripsBreakdown = [];
          this.tripsTotales = { ventas: 0, gastos: 0, combustible: 0, utilidad: 0 };
        }
      })
      .catch(err => {
        console.error("Error al cargar dashboard:", err);
      })
      .finally(() => {
        this.isLoading = false;
      });
    },

    fetchRecentTrips() {
      this.get({
        url: `${this.$store.getters.get__url}/transporte/trips`,
        token: this.$store.getters.get__token,
        params: {
          date_from: this.filters.date_from,
          date_to: this.filters.date_to,
          limit: 10
        }
      }).then((res) => {
        if (res.success) {
          this.recentTrips = res.data.slice(0, 10);
        }
      }).catch(err => {
        console.error("Error al cargar viajes recientes:", err);
      });
    }
  }
};
</script>

<style scoped>
.gap-2 { gap: 0.5rem !important; }

.bg-primary {
  background-color: #0F172A !important;
}

.bg-success {
  background-color: #2eb85c !important;
}

.bg-danger {
  background-color: #e55353 !important;
}

.bg-warning {
  background-color: #f9b115 !important;
}

.bg-info {
  background-color: #3399ff !important;
}

.bg-secondary {
  background-color: #6c757d !important;
}
</style>